import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { FacilityDetailsSummary } from '../../../../common/FacilityDetailsTabs/FacilityDetailsSummary';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import {
  providerEthnicity,
  providerRace,
} from '../../../../models/ProviderDetails';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import {
  getEthnicityDesc,
  getRaceDesc,
  isAttributesMatched,
} from '../../../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../../../utils/providerNames.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import {
  ADDITIONAL_ATTRIBUTE_NAMES,
  ReverseCoverageTypesCodes,
} from '../../../Constants';
import { mobileOnly, phoneOnly } from '../../../ConstantsStyles';
import { ContentWrapper } from '../../../ContentWrapper';
import { getFeatureFlag } from '../../../Utils/getFeatureFlag';
import { ContainerForAllSectionsStyled } from '../../ProviderDetailTabs.styled';

const textContainer = (phoneScreen) => ({
  maxWidth: '839px',
  fontWeight: '$medium',
  fontSize: 'medium',
  lineHeight: '24px',
  color: '#4B4D4F ',
  '& strong': {
    fontWeight: '$medium',
  },
  ...(phoneScreen && {
    fontSize: '$md',
    fontWeight: '500',
    lineHeight: '20px',
  }),
});

const subTextStyle = (phoneScreen) => ({
  height: '20px',
  fontWeight: 'normal',
  fontSize: '$md',
  lineHeight: '20px',
  color: '#4B4D4F',
  ...(phoneScreen && {
    fontSize: '14.22px',
  }),
});

const FlexContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  width: '100%',
});
const Section = styled('div', {
  width: '193px',
  minHeight: '44px',
  fontSize: '16px',
  color: '#4B4D4F ',
  lineHeight: '20px',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  '@media (max-width: 464px)': {
    maxWidth: '163px',
  },
});

const SectionHeading = styled('p', {
  dynamic: ({ cssProps: { isGEHAV2 } = {} }) => ({
    color: isGEHAV2 ? '$core.color.brand.100' : '',
    fontSize: '16px',
    marginBottom: '0px',
    fontWeight: 'bold',
  }),
});
const Dot = styled('div', {
  width: '4px',
  height: '4px',
  background: '#D9D9D9',
  margin: '$sm $xs $xs $xs',
  borderRadius: '50%',
  color: '#4B4D4F',
});
type Props = {
  heading: string;
  content?: string;
  gender: string;
  language: string[] | null;
  ethnicity?: string;
  providerRace?: providerRace[];
  providerEthnicity?: providerEthnicity[];
  providerType?: string;
  canShowAiProviderBio?: boolean;
  providerBioContent?: string;
  npi?: string;
};
export const BiographySection = ({
  heading,
  content,
  gender,
  language,
  ethnicity,
  providerEthnicity,
  providerRace,
  providerType,
  canShowAiProviderBio,
  providerBioContent,
  npi,
}: Props) => {
  const { t } = useTranslation();
  const coverageType = useCoverageType();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const mobileScreen = useMediaQuery(mobileOnly) || isWidget;
  const featureFlags = useLagoon('feature-flags')();
  const { policyNumber } = getCurrentMember();
  const additionalAttributes = useLagoon('additional-attributes')();

  const showRaceAndEthnicity = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.RACE_ETHNICITY
  );
  const showEthnicity: boolean = isAttributesMatched(
    additionalAttributes,
    coverageType,
    policyNumber,
    providerType,
    ADDITIONAL_ATTRIBUTE_NAMES.ETHNICITY
  );
  const showRace: boolean = isAttributesMatched(
    additionalAttributes,
    coverageType,
    policyNumber,
    providerType,
    ADDITIONAL_ATTRIBUTE_NAMES.RACE
  );

  const isBehavioralHealth =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];

  const notAvailableOrUnknownText = useNotAvailableUnknownText();

  const isGEHAV2 = shouldUseGehaV2Styling();
  const additionalIconCss = isGEHAV2 ? { color: '$core.color.brand.100' } : {};

  const getEthnicity = () => {
    if (isBehavioralHealth) {
      return (
        <Section>
          <SectionHeading
            cssProps={{ isGEHAV2 }}
            data-testid="ethnicity-heading"
          >
            {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.ETHNICITY')}
          </SectionHeading>
          <Text
            css={{ ...subTextStyle(phoneScreen), ...additionalIconCss }}
            data-auto-testid="ethnicity-text"
            data-testid="ethnicity-text"
          >
            {ethnicity
              ? capitalizeFirstLetters(ethnicity)
              : capitalizeFirstLetters(
                  notAvailableOrUnknownText?.providerDetails
                )}
          </Text>
        </Section>
      );
    }
    return null;
  };
  const getGender = (gender, t, phoneScreen) => (
    <Text
      css={{ ...subTextStyle(phoneScreen), ...additionalIconCss }}
      data-auto-testid="biography-gender"
      data-testid="biography-gender"
    >
      {capitalizeFirstLetters(
        t(`BIOGRAPHY_SECTION.GENDER.${gender?.toUpperCase() || 'FEMALE'}`)
      )}
    </Text>
  );

  const getLanguage = (language, phoneScreen) => {
    if (language) {
      return (
        <Text
          css={{ ...subTextStyle(phoneScreen), ...additionalIconCss }}
          data-auto-testid="biography-language"
          data-testid="biography-language"
        >
          {capitalizeFirstLetters(language?.join(', '))}
        </Text>
      );
    }
    return null;
  };
  const getBiograpghyContent = (content, phoneScreen) => (
    <Text
      as="div"
      css={{ ...textContainer(phoneScreen), ...additionalIconCss }}
      dangerouslySetInnerHTML={{
        __html: content || t('BIOGRAPHY_SECTION.BIOGRAPHY_NOT_AVAILABLE'),
      }}
      data-auto-testid="biography-text"
      data-testid="biography-text"
    />
  );
  return (
    <ContainerForAllSectionsStyled css={{ marginBottom: '8px' }}>
      <ContentWrapper isWidget={isWidget}>
        <Layout.Stack
          alignItems="left"
          css={{ gap: '4px', lineHeight: phoneScreen ? '20px' : '24px' }}
          data-auto-testid="about-tab-biography-section-container"
          data-testid="about-tab-biography-section-container"
        >
          {!canShowAiProviderBio ? (
            <>
              <Heading
                css={
                  mobileScreen
                    ? {
                        '&.abyss-heading-root': {
                          fontSize: '20.25px',
                          lineHeight: '24px',
                        },
                      }
                    : {}
                }
                data-auto-testid="biography-heading"
                data-testid="biography-heading"
                level={isWidget ? 3 : 2}
                size="sm"
              >
                {t(heading)}
              </Heading>

              {getBiograpghyContent(content, phoneScreen)}
            </>
          ) : (
            <FacilityDetailsSummary
              facilityBio={providerBioContent || ''}
              facilityTitle={t(heading)}
              npi={npi}
            />
          )}

          {showRaceAndEthnicity ? (
            <FlexContainer>
              <Section>
                <SectionHeading cssProps={{ isGEHAV2 }}>
                  {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.GENDER')}
                </SectionHeading>
                {getGender(gender, t, phoneScreen)}
              </Section>
              {language && (
                <Section>
                  <SectionHeading cssProps={{ isGEHAV2 }}>
                    {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.LANGUAGES')}
                  </SectionHeading>
                  {getLanguage(language, phoneScreen)}
                </Section>
              )}
              {showRace ? (
                <Section>
                  <SectionHeading
                    cssProps={{ isGEHAV2 }}
                    data-testid="race-heading"
                  >
                    {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.RACE')}
                  </SectionHeading>
                  <Text
                    css={{ ...subTextStyle(phoneScreen), ...additionalIconCss }}
                    data-auto-testid="biography-race"
                    data-testid="biography-race"
                  >
                    {getRaceDesc(providerRace, t)}
                  </Text>
                </Section>
              ) : null}
              {showEthnicity ? (
                <Section>
                  <SectionHeading
                    cssProps={{ isGEHAV2 }}
                    data-testid="ethnicity-heading"
                  >
                    {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.ETHNICITY')}
                  </SectionHeading>
                  <Text
                    css={{ ...subTextStyle(phoneScreen), ...additionalIconCss }}
                    data-auto-testid="biography-ethnicity"
                    data-testid="ethnicity-text"
                  >
                    {getEthnicityDesc(providerEthnicity, t)}
                  </Text>
                </Section>
              ) : (
                getEthnicity()
              )}
            </FlexContainer>
          ) : (
            <>
              <Flex css={{ flexWrap: 'inherit' }} justify="space-evenly">
                {getGender(gender, t, phoneScreen)}
                <Dot />
                {getLanguage(language, phoneScreen)}
              </Flex>
              {ethnicity && isBehavioralHealth && (
                <Text
                  css={subTextStyle(phoneScreen)}
                  data-auto-testid="ethnicity-text"
                  data-testid="ethnicity-text"
                >
                  {capitalizeFirstLetters(ethnicity)}
                </Text>
              )}
            </>
          )}
        </Layout.Stack>
      </ContentWrapper>
    </ContainerForAllSectionsStyled>
  );
};
